/**
 * iter553 — Dashboard Templates Section
 *
 * Renders inside the customizer drawer. Shows:
 *   • 3 built-in presets (Сутрешен фокус, Спортен режим, Пациентски прозорец)
 *   • Up to 25 user-saved templates
 *   • "Запази текущия" button — saves current order+hidden as a named template
 *   • One-click Apply / Delete (user templates only)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LayoutTemplate, Save, Check, Trash2, Star, Loader2, Sparkles,
  Sunrise, Dumbbell, Stethoscope,
} from "lucide-react";
import { useDashboardLayoutContext } from "@/dashboard/DashboardLayoutContext";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUILTIN_ICONS = {
  "builtin-morning-focus": Sunrise,
  "builtin-sport-mode": Dumbbell,
  "builtin-clinician-window": Stethoscope,
};

export default function DashboardTemplatesSection() {
  const { layout, setOrder, setHidden } = useDashboardLayoutContext();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/preferences/dashboard-templates`, { withCredentials: true });
      setTemplates(data?.templates || []);
    } catch {
      /* keep current list */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const applyTemplate = async (tpl) => {
    try {
      const { data } = await axios.post(
        `${API}/preferences/dashboard-templates/${tpl.id}/apply`,
        {},
        { withCredentials: true }
      );
      // Update local layout via context so UI re-renders immediately
      const newOrder = Array.isArray(data?.layout?.order) ? data.layout.order : [];
      const newHidden = Array.isArray(data?.layout?.hidden) ? data.layout.hidden : [];
      setOrder(newOrder);
      // Brute force: reset hidden by reading from server and force-sync
      // We rely on the next page navigation or hook to re-fetch.
      // Quick way: setHidden(id, true) for each new; (id, false) for those no longer hidden.
      const prevHidden = new Set(layout?.hidden || []);
      const nextHidden = new Set(newHidden);
      for (const id of prevHidden) {
        if (!nextHidden.has(id)) setHidden(id, false);
      }
      for (const id of nextHidden) {
        if (!prevHidden.has(id)) setHidden(id, true);
      }
      toast.success(bg ? `Шаблон „${tpl.name}" приложен ✓` : `Template "${tpl.name}" applied ✓`);
    } catch {
      toast.error(bg ? "Грешка при прилагане" : "Apply failed");
    }
  };

  const saveCurrentAs = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(bg ? "Въведи име" : "Enter a name");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/preferences/dashboard-templates`,
        { name: trimmed, layout: { order: layout?.order || [], hidden: layout?.hidden || [] } },
        { withCredentials: true }
      );
      toast.success(bg ? `Шаблон „${trimmed}" запазен ✓` : `Template "${trimmed}" saved ✓`);
      setName("");
      await fetchTemplates();
    } catch {
      toast.error(bg ? "Неуспешно запазване" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (tpl.is_builtin) return;
    if (!window.confirm(bg ? `Изтрий „${tpl.name}"?` : `Delete "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/preferences/dashboard-templates/${tpl.id}`, { withCredentials: true });
      toast.success(bg ? "Изтрит" : "Deleted");
      await fetchTemplates();
    } catch {
      toast.error(bg ? "Грешка при изтриване" : "Delete failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="dashboard-templates-section">
      <div className="flex items-center gap-2">
        <LayoutTemplate className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
        <h3 className={`text-[12.5px] font-bold uppercase tracking-wider ${
          isDark ? "text-slate-200" : "text-slate-700"
        }`}>
          {bg ? "Шаблони от Dashboard" : "Dashboard templates"}
        </h3>
      </div>

      {loading ? (
        <div className={`text-[11.5px] py-3 text-center ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
          {bg ? "Зареждам…" : "Loading…"}
        </div>
      ) : (
        <div className="space-y-1.5">
          {templates.map((tpl) => {
            const Icon = BUILTIN_ICONS[tpl.id] || Sparkles;
            return (
              <div
                key={tpl.id}
                data-testid={`template-row-${tpl.id}`}
                className={`flex items-center gap-2 p-2.5 rounded-xl border transition-colors ${
                  isDark
                    ? "border-slate-700 bg-slate-800/60 hover:border-violet-500/40"
                    : "border-slate-200 bg-white hover:border-violet-300"
                }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                  tpl.is_builtin
                    ? (isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-600")
                    : (isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600")
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[12.5px] font-bold truncate ${
                    isDark ? "text-slate-100" : "text-slate-800"
                  }`}>
                    {tpl.name}
                    {tpl.is_builtin && (
                      <Star className="w-2.5 h-2.5 inline ml-1 text-amber-500 fill-amber-500" />
                    )}
                  </div>
                  <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                    {(tpl.layout?.order?.length || 0)} {bg ? "подреден" : "ordered"} ·{" "}
                    {(tpl.layout?.hidden?.length || 0)} {bg ? "скрити" : "hidden"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10.5px] gap-1"
                  onClick={() => applyTemplate(tpl)}
                  data-testid={`template-apply-${tpl.id}`}
                >
                  <Check className="w-3 h-3" />
                  {bg ? "Приложи" : "Apply"}
                </Button>
                {!tpl.is_builtin && (
                  <button
                    type="button"
                    onClick={() => deleteTemplate(tpl)}
                    aria-label={bg ? "Изтрий" : "Delete"}
                    data-testid={`template-delete-${tpl.id}`}
                    className={`p-1.5 rounded-lg ${
                      isDark
                        ? "text-rose-300 hover:bg-rose-500/15"
                        : "text-rose-600 hover:bg-rose-50"
                    }`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Save current */}
      <div className={`rounded-xl p-2.5 border ${
        isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
      }`}>
        <div className={`text-[10.5px] font-bold uppercase tracking-wider mb-1.5 ${
          isDark ? "text-slate-400" : "text-slate-500"
        }`}>
          {bg ? "Запази текущата подредба" : "Save current layout"}
        </div>
        <div className="flex items-center gap-1.5">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={bg ? "Име на шаблона…" : "Template name…"}
            data-testid="template-name-input"
            className="h-8 text-[12px]"
            maxLength={60}
          />
          <Button
            size="sm"
            onClick={saveCurrentAs}
            disabled={saving || !name.trim()}
            data-testid="template-save-btn"
            className="h-8 gap-1 text-[11px]"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {bg ? "Запази" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
